import { useCheckoutContext } from '../index'
import type { Validate } from '@ztechapps/common.hook'
import { useAccessToken, useError } from '@ztechapps/common.hook'
import { useStripe as useStripeCore } from '@ztechapps/common.hook/useStripe'
import compact from 'lodash/compact'
import { fetch } from '@ztechapps/common.utils'
import isEmpty from 'lodash/isEmpty'
import { useLocation } from 'react-use'
import merge from 'lodash/merge'
import type { PaymentOptions } from '../types'
import { usePaymentValidation } from './usePaymentValidation'
import trim from 'lodash/trim'

type StripeLineItem = { price: number; name: string }

const useStartCardPaymentBase = (validate?: Validate<PaymentOptions>) => {
  const checkoutContext = useCheckoutContext()
  const { document } = checkoutContext.orderDocumentResult
  const { error, onError } = useError()
  const fromCheckout = checkoutContext.fromCheckout
  const appSettings = checkoutContext.appSettings
  let success_url = checkoutContext.success_url
  let cancel_url = checkoutContext.cancel_url
  const orderNetAmount = checkoutContext.orderNetAmount

  const location = useLocation()

  if (success_url === 'USE_CURRENT') {
    const successUrl = new URL(location.href)
    successUrl.searchParams.set('paymentStatus', 'success')
    success_url = successUrl.href
  }
  if (cancel_url === 'USE_CURRENT') {
    const cancelUrl = new URL(location.href)
    cancelUrl.searchParams.set('paymentStatus', 'cancelled')
    cancel_url = cancelUrl.href
  }

  success_url = success_url ?? `${appSettings?.RootUrl}/checkout/success`
  cancel_url = cancel_url ?? `${appSettings?.RootUrl}/checkout/cancel`

  const [, setShowLoadingOverlay] = checkoutContext.showLoadingOverlay
  const accessToken = useAccessToken()
  const orderId = document?._id
  const stripePromise = useStripe()

  const internalValidate: Validate<PaymentOptions> = (args?: PaymentOptions) => {
    //do the validation check if it was provided

    const validateErrors = validate?.(args) ?? {}
    const newErrors: Record<string, any> = {}

    //for card payment the minimum amount to be paid should be zero 0.5
    if (args.amount <= 0.5) {
      newErrors.amount = 'Minimum amount to be paid is $0.5'
    }

    if (!orderId) {
      newErrors.orderId = 'Order Id is required'
    }
    if (!orderNetAmount) {
      newErrors.orderNetAmount = 'Order Net Amount to be paid is required'
    }
    if (!accessToken) {
      newErrors.accessToken = 'Access token is required'
    }

    return merge({}, newErrors, validateErrors)
  }

  const onStartPayment = async (partialPaymentLineItem: StripeLineItem, shouldNotRedirectToCheckout?: boolean) => {
    try {
      setShowLoadingOverlay(true)

      const newLineItems = compact(isEmpty(partialPaymentLineItem) ? [] : [partialPaymentLineItem])

      const stripe = await stripePromise
      const url = `/api/stripe/create-checkout-session`

      const { sessionId, url: sessionUrl }: any = await fetch(url, {
        headers: {
          'totalmilk-login-token': accessToken,
        },
        method: 'POST',
        body: JSON.stringify({
          amount: orderNetAmount,
          orderId,
          userId: document?.by?._id,
          success_url,
          cancel_url,
          line_items: newLineItems,
          fromCheckout,
        }),
      })

      if (shouldNotRedirectToCheckout) {
        return sessionUrl
      }
      // When the customer clicks on the button, redirect them to Checkout.
      const result = await stripe.redirectToCheckout({
        sessionId,
      })
      if (result.error) {
        // If `redirectToCheckout` fails due to a browser or network
        // error, display the localized error message to your customer
        // using `result.error.message`.
        throw new Error(result?.error?.message)
      }
    } catch (e) {
      onError(e)
      throw e
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return { onStartPayment, hasError: !isEmpty(error), error, validate: internalValidate }
}

export const useStartCardPayment = (validate?: Validate<PaymentOptions>) => {
  const { onStartPayment, ...rest } = useStartCardPaymentBase(validate)
  const checkoutContext = useCheckoutContext()
  const [paymentOptions] = checkoutContext.paymentOptions
  const hasPartialPayments = checkoutContext.hasPartialPayments

  const remarks = paymentOptions.remarks
  const amount = paymentOptions.amount
  const partialPaymentLineItem: StripeLineItem = hasPartialPayments
    ? {
        name: isEmpty(trim(remarks)) ? 'Additional payment.' : remarks,
        price: amount,
      }
    : null

  const _onStartPayment = ({ shouldNotRedirectToCheckout = false }: { shouldNotRedirectToCheckout?: boolean } = {}) =>
    onStartPayment(partialPaymentLineItem, shouldNotRedirectToCheckout)

  return { ...rest, onStartPayment: _onStartPayment }
}

export const useStartPartialCardPayment = () => {
  const validate = usePaymentValidation()
  const checkoutContext = useCheckoutContext()
  const [paymentOptions] = checkoutContext.paymentOptions

  const { onStartPayment, ...rest } = useStartCardPaymentBase(validate)

  const _onStartPayment = (args?: PaymentOptions) => {
    const remarks = args?.remarks ?? paymentOptions.remarks
    const amount = args?.amount ?? paymentOptions.amount
    const shouldNotRedirectToCheckout = args?.shouldNotRedirectToCheckout
    const partialPaymentLineItem: StripeLineItem = {
      name: isEmpty(trim(remarks)) ? 'Additional payment.' : remarks,
      price: amount,
    }

    return onStartPayment(partialPaymentLineItem, shouldNotRedirectToCheckout)
  }

  return { ...rest, onStartPayment: _onStartPayment }
}

export const useStripe = () => {
  const checkoutContext = useCheckoutContext()
  const appSettings = checkoutContext.appSettings
  const publishableKey = appSettings?.Stripe.publishableKey

  return useStripeCore(publishableKey)
}
